import React, { useState, useMemo, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { BarChart3, FileText, Download, Filter, TrendingUp, Users, Package, Truck, DollarSign, Activity, Clock, Award, AlertCircle, ArrowLeft } from 'lucide-react';
import RoleBasedHeader from 'components/ui/RoleBasedHeader';
import ReportFilters from './components/ReportFilters';
import ReportCard from './components/ReportCard';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../lib/supabase';
import DateRangeFilter, { getDateRange } from '../../components/DateRangeFilter';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

// Format a date string or Date object to "DD MMM YYYY"
const formatDate = (dateInput) => {
  if (!dateInput) return '';
  const d = new Date(dateInput);
  if (isNaN(d?.getTime())) return String(dateInput);
  const day = String(d?.getDate())?.padStart(2, '0');
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const month = months?.[d?.getMonth()];
  const year = d?.getFullYear();
  return `${day} ${month} ${year}`;
};

// Parse a date string to a Date object (handles ISO, DD/MM/YYYY, YYYY-MM-DD)
const parseDate = (dateStr) => {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (!isNaN(d?.getTime())) return d;
  return null;
};

const ReportsGenerationCenter = () => {
  const [currentRole, setCurrentRole] = useState(() => localStorage.getItem('userRole') || 'buyer');
  const [selectedCategory, setSelectedCategory] = useState('all');  
  const [datePreset, setDatePreset] = useState('till_date');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [customFilters, setCustomFilters] = useState({});
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();

  // ── Real data from Supabase buyer_bookings (approved only) + trade_payments ─
  const [buyerBookings, setBuyerBookings] = useState([]);

  useEffect(() => {
    const fetchBuyerBookings = async () => {
      if (!user?.id) return;
      // Only approved bookings — quantity and amount must reflect what was actually approved
      const { data: bookings, error } = await supabase
        ?.from('buyer_bookings')
        ?.select('*')
        ?.eq('buyer_id', user?.id)
        ?.in('status', ['approved', 'do_generated', 'in_transit', 'completed', 'delivered']);        
      console.log('[ReportsCenter] buyer_bookings query (approved only):', { userId: user?.id, bookings, error });
      if (!bookings || bookings.length === 0) { setBuyerBookings([]); return; }

      // Fetch trade_payments for TDS data
      const bookingIds = bookings.map(b => b.id);
      const { data: payments } = await supabase
        ?.from('trade_payments')
        ?.select('booking_id, amount, tds_deducted, net_amount_paid, payment_method')
        ?.in('booking_id', bookingIds);

      // Attach payment data to each booking
      const paymentsMap = {};
      (payments || []).forEach(p => { paymentsMap[p.booking_id] = p; });
      const enriched = bookings.map(b => ({
        ...b,
        _payment: paymentsMap[b.id] || null,
      }));
      setBuyerBookings(enriched);
    };
    fetchBuyerBookings();
  }, [user]);

  // ── Date-filtered bookings ────────────────────────────────────────────────
  const filteredBookings = useMemo(() => {
    const { from, to } = getDateRange(datePreset, customFrom, customTo);
    return buyerBookings?.filter(booking => {
      const bookingDate = parseDate(booking?.created_at || booking?.delivery_date);
      if (!bookingDate) return true;
      if (from && bookingDate < from) return false;
      if (to && bookingDate > to) return false;
      return true;
    });
  }, [buyerBookings, datePreset, customFrom, customTo]);

  // ── Purchase Analytics aggregations ──────────────────────────────────────
  const purchaseAnalyticsData = useMemo(() => {
    const allOrders = filteredBookings;

    // --- Purchase History (line chart): total spend per month ---
    const monthlySpend = {};
    const monthNames = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    allOrders?.forEach(order => {
      const dateStr = order?.created_at || order?.delivery_date;
      const d = parseDate(dateStr);
      const monthKey = d ? monthNames?.[d?.getMonth()] : 'Unknown';
      const qty = parseFloat(order?.quantity || 0);
      const price = parseFloat(order?.rate_per_ton || 0);
      const spend = qty * price;
      if (!monthlySpend?.[monthKey]) monthlySpend[monthKey] = { value: 0, target: 0, count: 0 };
      monthlySpend[monthKey].value += spend;
      monthlySpend[monthKey].count += 1;
    });

    let purchaseHistoryData = monthNames?.filter(m => monthlySpend?.[m])?.map(m => ({
        name: m,
        value: Math.round(monthlySpend?.[m]?.value),
        target: Math.round(monthlySpend?.[m]?.value * 1.1),
        count: monthlySpend?.[m]?.count
      }));

    if (purchaseHistoryData?.length === 0) {
      purchaseHistoryData = monthNames?.slice(0, 6)?.map(m => ({ name: m, value: 0, target: 0, count: 0 }));
    }

    // --- Cost Trends (area chart): avg price per ton per month ---
    const monthlyPrices = {};
    allOrders?.forEach(order => {
      const dateStr = order?.created_at || order?.delivery_date;
      const d = parseDate(dateStr);
      const monthKey = d ? monthNames?.[d?.getMonth()] : 'Unknown';
      const price = parseFloat(order?.rate_per_ton || 0);
      if (price > 0) {
        if (!monthlyPrices?.[monthKey]) monthlyPrices[monthKey] = { total: 0, count: 0 };
        monthlyPrices[monthKey].total += price;
        monthlyPrices[monthKey].count += 1;
      }
    });

    let costTrendData = monthNames?.filter(m => monthlyPrices?.[m])?.map(m => ({
        name: m,
        value: Math.round(monthlyPrices?.[m]?.total / monthlyPrices?.[m]?.count),
        target: Math.round((monthlyPrices?.[m]?.total / monthlyPrices?.[m]?.count) * 0.95)
      }));

    if (costTrendData?.length === 0) {
      costTrendData = monthNames?.slice(0, 6)?.map(m => ({ name: m, value: 0, target: 0 }));
    }

    // --- Spending Analytics (bar chart): spend by factory/supplier ---
    const factorySpend = {};
    allOrders?.forEach(order => {
      const factory = order?.factory_name || order?.mill_name || 'Unknown';
      const qty = parseFloat(order?.quantity || 0);
      const price = parseFloat(order?.rate_per_ton || 0);
      const spend = qty * price;
      if (!factorySpend?.[factory]) factorySpend[factory] = { value: 0, target: 0 };
      factorySpend[factory].value += spend;
      factorySpend[factory].target += spend * 1.1;
    });

    let spendingData = Object.entries(factorySpend)?.map(([name, d]) => ({ name: name?.length > 10 ? name?.slice(0, 10) + '…' : name, value: Math.round(d?.value), target: Math.round(d?.target) }))?.sort((a, b) => b?.value - a?.value)?.slice(0, 6);

    if (spendingData?.length === 0) {
      spendingData = [{ name: 'No Data', value: 0, target: 0 }];
    }

    // --- Order Frequency (line chart, count unit): orders placed per month ---
    let orderFrequencyData = monthNames?.filter(m => monthlySpend?.[m])?.map(m => ({
        name: m,
        value: monthlySpend?.[m]?.count,
        target: Math.round(monthlySpend?.[m]?.count * 1.1)
      }));

    if (orderFrequencyData?.length === 0) {
      orderFrequencyData = monthNames?.slice(0, 6)?.map(m => ({ name: m, value: 0, target: 0 }));
    }

    // --- Vendor Comparison (bar chart): avg price paid per factory ---
    const factoryPrices = {};
    allOrders?.forEach(order => {
      const factory = order?.factory_name || order?.mill_name || 'Unknown';
      const price = parseFloat(order?.rate_per_ton || 0);
      if (price > 0) {
        if (!factoryPrices?.[factory]) factoryPrices[factory] = { total: 0, count: 0 };
        factoryPrices[factory].total += price;
        factoryPrices[factory].count += 1;
      }
    });

    let vendorPriceData = Object.entries(factoryPrices)?.map(([name, d]) => ({
        name: name?.length > 10 ? name?.slice(0, 10) + '…' : name,
        value: Math.round(d?.total / d?.count),
        target: Math.round((d?.total / d?.count) * 0.95)
      }))?.sort((a, b) => a?.value - b?.value)?.slice(0, 6);

    if (vendorPriceData?.length === 0) {
      vendorPriceData = [{ name: 'No Data', value: 0, target: 0 }];
    }

    // --- Payment Summaries (pie chart): recorded payments by method vs bookings with no payment record yet ---
    // Note: trade_payments.status/payment_status are not reliably updated (observed uniformly 'pending'
    // across all rows), so "paid" here means a trade_payments row exists for the booking, not that column.
    const methodBreakdown = {};
    let unrecordedAmount = 0;
    allOrders?.forEach(order => {
      if (order?._payment) {
        const method = order?._payment?.payment_method || 'Unknown';
        methodBreakdown[method] = (methodBreakdown?.[method] || 0) + parseFloat(order?._payment?.amount || 0);
      } else {
        unrecordedAmount += parseFloat(order?.total_amount || 0);
      }
    });

    let paymentSummaryData = Object.entries(methodBreakdown)?.map(([name, value]) => ({ name, value: Math.round(value) }));
    if (unrecordedAmount > 0) paymentSummaryData?.push({ name: 'No Payment Recorded Yet', value: Math.round(unrecordedAmount) });
    if (paymentSummaryData?.length === 0) paymentSummaryData = [{ name: 'No Data', value: 0 }];

    // Summary metrics — approved bookings only, correct rate_per_ton
    const totalSpend = allOrders?.reduce((sum, o) => {
      const qty = parseFloat(o?.quantity || 0);
      const price = parseFloat(o?.rate_per_ton || 0);
      return sum + qty * price;
    }, 0);
    const totalMT = allOrders?.reduce((sum, o) => sum + parseFloat(o?.quantity || 0), 0);
    const totalTdsDeducted = allOrders?.reduce((sum, o) => sum + parseFloat(o?._payment?.tds_deducted || 0), 0);
    const totalNetReceived = allOrders?.reduce((sum, o) => sum + parseFloat(o?._payment?.net_amount_paid || 0), 0);
    const totalOrders = filteredBookings?.length;
    const avgPrice = allOrders?.length > 0
      ? allOrders?.reduce((sum, o) => sum + parseFloat(o?.rate_per_ton || 0), 0) / allOrders?.length
      : 0;

    return {
      purchaseHistoryData,
      costTrendData,
      spendingData,
      orderFrequencyData,
      vendorPriceData,
      paymentSummaryData,
      summary: {
        totalSpend: Math.round(totalSpend),
        totalOrders,
        totalMT: Math.round(totalMT),
        totalTdsDeducted: Math.round(totalTdsDeducted),
        totalNetReceived: Math.round(totalNetReceived),
        avgPrice: Math.round(avgPrice)
      }
    };
  }, [filteredBookings]);

  // Comprehensive report categories by role
  const reportCategories = useMemo(() => ({
    buyer: [
      {
        id: 'purchase-analytics',
        name: 'Purchase Analytics',
        icon: BarChart3,
        color: 'blue',
        reports: [
          {
            id: 'purchase-history',
            title: 'Purchase History Analysis',
            description: 'Detailed breakdown of all sugar purchases with trends',
            metrics: ['Total Purchases', 'Average Order Value', 'Purchase Frequency'],
            chartType: 'line',
            realData: purchaseAnalyticsData?.purchaseHistoryData,
            summary: purchaseAnalyticsData?.summary
          },
          {
            id: 'cost-trends',
            title: 'Cost Trend Reports',
            description: 'Historical cost analysis and price fluctuations',
            metrics: ['Average Cost per Ton', 'Cost Variance', 'Savings Achieved'],
            chartType: 'area',
            realData: purchaseAnalyticsData?.costTrendData,
            summary: purchaseAnalyticsData?.summary
          },
          {
            id: 'spending-analytics',
            title: 'Spending Analytics',
            description: 'Spending breakdown by factory/supplier',
            metrics: ['Total Spend', 'Category Breakdown', 'Budget Utilization'],
            chartType: 'bar',
            realData: purchaseAnalyticsData?.spendingData,
            summary: purchaseAnalyticsData?.summary
          }
        ]
      },
      {
        id: 'vendor-performance',
        name: 'Vendor Performance',
        icon: Award,
        color: 'green',
        reports: [
          {
            id: 'vendor-metrics',
            title: 'Vendor Performance Metrics',
            description: 'Supplier reliability and quality ratings',
            metrics: ['On-time Delivery', 'Quality Score', 'Response Time'],
            chartType: 'radar',
            comingSoon: true
          },
          {
            id: 'vendor-comparison',
            title: 'Vendor Comparison Analysis',
            description: 'Average price paid per factory/supplier',
            metrics: ['Price Competitiveness'],
            chartType: 'bar',
            realData: purchaseAnalyticsData?.vendorPriceData,
            summary: purchaseAnalyticsData?.summary
          }
        ]
      },
      {
        id: 'payment-reports',
        name: 'Payment Reports',
        icon: DollarSign,
        color: 'purple',
        reports: [
          {
            id: 'payment-summary',
            title: 'Payment Summaries',
            description: 'Recorded payments by method, and bookings with no payment on record',
            metrics: ['Payment Method Breakdown', 'Unrecorded Amount'],
            chartType: 'pie',
            realData: purchaseAnalyticsData?.paymentSummaryData,
            summary: purchaseAnalyticsData?.summary
          },
          {
            id: 'credit-analysis',
            title: 'Credit Utilization Analysis',
            description: 'Credit usage patterns and payment terms',
            metrics: ['Credit Limit', 'Utilization %', 'Payment Cycle'],
            chartType: 'gauge'
          }
        ]
      },
      {
        id: 'order-analytics',
        name: 'Order Analytics',
        icon: Package,
        color: 'orange',
        reports: [
          {
            id: 'order-frequency',
            title: 'Order Frequency Reports',
            description: 'Number of orders placed per month',
            metrics: ['Orders per Month'],
            chartType: 'line',
            valueUnit: 'count',
            realData: purchaseAnalyticsData?.orderFrequencyData,
            summary: purchaseAnalyticsData?.summary
          },
          {
            id: 'fulfillment-rate',
            title: 'Order Fulfillment Analysis',
            description: 'Delivery success rates and delays',
            metrics: ['Fulfillment Rate', 'Average Delivery Time', 'Delays'],
            chartType: 'bar',
            comingSoon: true
          }
        ]
      }
    ],
    factory: [
      {
        id: 'production-analytics',
        name: 'Production Analytics',
        icon: Activity,
        color: 'blue',
        reports: [
          {
            id: 'production-output',
            title: 'Production Output Reports',
            description: 'Daily, weekly, and monthly production metrics',
            metrics: ['Total Production', 'Grade Distribution', 'Efficiency Rate'],
            chartType: 'bar',
            comingSoon: true
          },
          {
            id: 'operational-efficiency',
            title: 'Operational Efficiency Analysis',
            description: 'Machine utilization and downtime analysis',
            metrics: ['Uptime %', 'Downtime Analysis', 'Maintenance Schedule'],
            chartType: 'line',
            comingSoon: true
          }
        ]
      },
      {
        id: 'sales-performance',
        name: 'Sales Performance',
        icon: TrendingUp,
        color: 'green',
        reports: [
          {
            id: 'sales-tracking',
            title: 'Sales Performance Tracking',
            description: 'Revenue and sales volume analysis',
            metrics: ['Total Sales', 'Revenue Growth', 'Market Share'],
            chartType: 'area'
          },
          {
            id: 'grade-wise-sales',
            title: 'Grade-wise Sales Breakdown',
            description: 'Sales distribution by sugar grades',
            metrics: ['S-30 Sales', 'M-30 Sales', 'Premium Grade Sales'],
            chartType: 'pie'
          }
        ]
      },
      {
        id: 'inventory-reports',
        name: 'Inventory Reports',
        icon: Package,
        color: 'purple',
        reports: [
          {
            id: 'inventory-turnover',
            title: 'Inventory Turnover Analysis',
            description: 'Stock movement and turnover rates',
            metrics: ['Turnover Ratio', 'Days in Inventory', 'Stock Levels'],
            chartType: 'bar',
            comingSoon: true
          },
          {
            id: 'stock-aging',
            title: 'Stock Aging Reports',
            description: 'Analysis of inventory age and shelf life',
            metrics: ['Fresh Stock %', 'Aging Stock', 'Expiry Alerts'],
            chartType: 'stacked-bar',
            comingSoon: true
          }
        ]
      },
      {
        id: 'revenue-dashboards',
        name: 'Revenue Dashboards',
        icon: DollarSign,
        color: 'orange',
        reports: [
          {
            id: 'revenue-analysis',
            title: 'Revenue Analysis Dashboard',
            description: 'Comprehensive revenue metrics and forecasts',
            metrics: ['Total Revenue', 'Profit Margin', 'Revenue per Grade'],
            chartType: 'area'
          },
          {
            id: 'payment-collection',
            title: 'Payment Collection Reports',
            description: 'Receivables and collection efficiency',
            metrics: ['Collections', 'Outstanding Receivables', 'Collection Rate'],
            chartType: 'line'
          }
        ]
      }
    ],
    transporter: [
      {
        id: 'trip-analytics',
        name: 'Trip Analytics',
        icon: Truck,
        color: 'blue',
        reports: [
          {
            id: 'trip-efficiency',
            title: 'Trip Efficiency Reports',
            description: 'Delivery performance and route optimization',
            metrics: ['On-time Deliveries', 'Average Trip Duration', 'Route Efficiency'],
            chartType: 'bar',
            comingSoon: true
          },
          {
            id: 'route-optimization',
            title: 'Route Optimization Analysis',
            description: 'Most efficient routes and cost savings',
            metrics: ['Optimal Routes', 'Distance Saved', 'Fuel Saved'],
            chartType: 'line',
            comingSoon: true
          }
        ]
      },
      {
        id: 'vehicle-utilization',
        name: 'Vehicle Utilization',
        icon: Activity,
        color: 'green',
        reports: [
          {
            id: 'utilization-metrics',
            title: 'Vehicle Utilization Metrics',
            description: 'Fleet usage and capacity optimization',
            metrics: ['Utilization Rate', 'Idle Time', 'Active Vehicles'],
            chartType: 'gauge'
          },
          {
            id: 'maintenance-tracking',
            title: 'Maintenance & Downtime Reports',
            description: 'Vehicle health and maintenance schedules',
            metrics: ['Maintenance Costs', 'Downtime Hours', 'Service Due'],
            chartType: 'bar'
          }
        ]
      },
      {
        id: 'earnings-analytics',
        name: 'Earnings Analytics',
        icon: DollarSign,
        color: 'purple',
        reports: [
          {
            id: 'earnings-analysis',
            title: 'Earnings Analysis',
            description: 'Revenue breakdown by trips and vehicles',
            metrics: ['Total Earnings', 'Per-Trip Revenue', 'Payment Status'],
            chartType: 'area'
          },
          {
            id: 'fuel-cost-analysis',
            title: 'Fuel Cost Analysis',
            description: 'Fuel consumption and cost optimization',
            metrics: ['Total Fuel Cost', 'Mileage', 'Cost per KM'],
            chartType: 'line',
            comingSoon: true
          }
        ]
      },
      {
        id: 'performance-scorecard',
        name: 'Performance Scorecards',
        icon: Award,
        color: 'orange',
        reports: [
          {
            id: 'driver-performance',
            title: 'Driver Performance Reports',
            description: 'Individual driver metrics and ratings',
            metrics: ['Safety Score', 'Customer Rating', 'Completed Trips'],
            chartType: 'radar'
          },
          {
            id: 'service-quality',
            title: 'Service Quality Metrics',
            description: 'Customer satisfaction and service ratings',
            metrics: ['Overall Rating', 'Complaint Resolution', 'Repeat Customers'],
            chartType: 'bar',
            comingSoon: true
          }
        ]
      }
    ],
    admin: [
      {
        id: 'platform-analytics',
        name: 'Platform Analytics',
        icon: BarChart3,
        color: 'blue',
        reports: [
          {
            id: 'platform-overview',
            title: 'Platform-wide Analytics',
            description: 'Overall platform performance and growth',
            metrics: ['Total Users', 'Active Transactions', 'Growth Rate'],
            chartType: 'line'
          },
          {
            id: 'user-engagement',
            title: 'User Engagement Reports',
            description: 'User activity and engagement metrics',
            metrics: ['Daily Active Users', 'Session Duration', 'Feature Usage'],
            chartType: 'area',
            comingSoon: true
          }
        ]
      },
      {
        id: 'transaction-reports',
        name: 'Transaction Reports',
        icon: DollarSign,
        color: 'green',
        reports: [
          {
            id: 'transaction-summary',
            title: 'Transaction Summaries',
            description: 'Complete transaction volume and values',
            metrics: ['Total Transactions', 'Transaction Value', 'Success Rate'],
            chartType: 'bar'
          },
          {
            id: 'revenue-analytics',
            title: 'Platform Revenue Analytics',
            description: 'Commission and revenue generation',
            metrics: ['Platform Revenue', 'Commission Earned', 'Revenue Growth'],
            chartType: 'area'
          }
        ]
      },
      {
        id: 'compliance-docs',
        name: 'Compliance Documentation',
        icon: FileText,
        color: 'purple',
        reports: [
          {
            id: 'kyc-reports',
            title: 'KYC Compliance Reports',
            description: 'User verification and compliance status',
            metrics: ['Verified Users', 'Pending Verifications', 'Compliance Rate'],
            chartType: 'pie'
          },
          {
            id: 'regulatory-compliance',
            title: 'Regulatory Compliance Reports',
            description: 'Legal and regulatory adherence tracking',
            metrics: ['Compliance Score', 'Audit Findings', 'Action Items'],
            chartType: 'bar',
            comingSoon: true
          }
        ]
      },
      {
        id: 'user-activity',
        name: 'User Activity Insights',
        icon: Users,
        color: 'orange',
        reports: [
          {
            id: 'role-wise-activity',
            title: 'Role-wise Activity Reports',
            description: 'Activity breakdown by user roles',
            metrics: ['Buyers Activity', 'Factory Activity', 'Transporter Activity'],
            chartType: 'stacked-bar',
            comingSoon: true
          },
          {
            id: 'system-health',
            title: 'System Health Reports',
            description: 'Platform performance and uptime metrics',
            metrics: ['Uptime %', 'Response Time', 'Error Rate'],
            chartType: 'line',
            comingSoon: true
          }
        ]
      }
    ]
  }), [purchaseAnalyticsData]);

  // Filter reports based on selected category
  const filteredReports = useMemo(() => {
    if (selectedCategory === 'all') {
      return reportCategories?.[currentRole] || [];
    }
    return (reportCategories?.[currentRole] || [])?.filter(cat => cat?.id === selectedCategory);
  }, [currentRole, selectedCategory, reportCategories]);

  const roleColors = {
    buyer: 'blue',
    factory: 'green',
    transporter: 'orange',
    admin: 'purple'
  };

  const roleLabels = {
    buyer: 'Buyer',
    factory: 'Factory',
    transporter: 'Transporter',
    admin: 'Admin'
  };

  return (
    <>
      <Helmet>
        <title>Reports Generation Center - SugarHathi | Comprehensive Analytics</title>
        <meta name="description" content="Generate comprehensive reports and analytics for your SugarHathi operations. Access role-based insights, export data, and schedule automated reports." />
      </Helmet>
      <div className="min-h-screen" style={{ background: '#f8f1e9' }}>
        <RoleBasedHeader 
          currentRole={currentRole} 
          onRoleChange={setCurrentRole}
        />

        <div className="container mx-auto px-4 py-8 max-w-7xl">
          {/* Header Section */}
          <div className="mb-8">
            {currentRole === 'buyer' && (
              <button
                onClick={() => navigate('/buyer-operations-dashboard')}
                className="flex items-center gap-2 text-[#1e4a72] hover:text-[#ff8c00] font-medium mb-4 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Dashboard
              </button>
            )}
            <div className="flex items-center gap-3 mb-2">
              <BarChart3 className="w-8 h-8 text-[#1e4a72]" />
              <h1 className="text-3xl font-bold text-gray-900">{t('reports.title')}</h1>
            </div>
            <p className="text-gray-600 text-lg">
              Comprehensive analytics and reporting for {roleLabels?.[currentRole]} operations
            </p>
          </div>

          {/* Filters Section */}
          <ReportFilters
            currentRole={currentRole}
            categories={reportCategories?.[currentRole] || []}
            selectedCategory={selectedCategory}
            onCategoryChange={setSelectedCategory}
            dateRange={{ from: '', to: '' }}
            onDateRangeChange={() => {}}
            customFilters={customFilters}
            onCustomFiltersChange={setCustomFilters}
            formatDate={formatDate}
          />
          <div className="mb-8">
            <DateRangeFilter
              preset={datePreset}
              customFrom={customFrom}
              customTo={customTo}
              onPresetChange={setDatePreset}
              onCustomFromChange={setCustomFrom}
              onCustomToChange={setCustomTo}
            />
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-blue-500">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Available Reports</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {(reportCategories?.[currentRole] || [])?.reduce((acc, cat) => acc + cat?.reports?.length, 0)}
                  </p>
                </div>
                <FileText className="w-10 h-10 text-blue-500 opacity-20" />
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-green-500">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Categories</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {(reportCategories?.[currentRole] || [])?.length}
                  </p>
                </div>
                <Filter className="w-10 h-10 text-green-500 opacity-20" />
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-purple-500">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Total Orders</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {currentRole === 'buyer' ? purchaseAnalyticsData?.summary?.totalOrders : '—'}
                  </p>
                </div>
                <Download className="w-10 h-10 text-purple-500 opacity-20" />
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-orange-500">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">
                    {currentRole === 'buyer' ? 'Total Spend' : 'Scheduled Reports'}
                  </p>
                  <p className="text-2xl font-bold text-gray-900">
                    {currentRole === 'buyer'
                      ? (purchaseAnalyticsData?.summary?.totalSpend > 0
                          ? `₹${(purchaseAnalyticsData?.summary?.totalSpend / 100000)?.toFixed(1)}L`
                          : '₹0')
                      : '—'}
                  </p>
                </div>
                <Clock className="w-10 h-10 text-orange-500 opacity-20" />
              </div>
            </div>
          </div>

          {/* Reports Grid */}
          {filteredReports?.length > 0 ? (
            <div className="space-y-8">
              {filteredReports?.map((category) => (
                <div key={category?.id} className="bg-white rounded-xl shadow-lg p-6">
                  <div className="flex items-center gap-3 mb-6 pb-4 border-b">
                    <div className={`p-3 rounded-lg bg-${category?.color}-50`}>
                      <category.icon className={`w-6 h-6 text-${category?.color}-600`} />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-gray-900">{category?.name}</h2>
                      <p className="text-sm text-gray-600">{category?.reports?.length} reports available</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {category?.reports?.map((report) => (
                      <ReportCard
                        key={report?.id}
                        report={report}
                        categoryColor={category?.color}
                        formatDate={formatDate}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-lg p-12 text-center">
              <AlertCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No Reports Available</h3>
              <p className="text-gray-600">
                Try adjusting your filters or select a different category
              </p>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ReportsGenerationCenter;
